<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { PROJECT_SUBMODULES } from '@qb/shared';
import LocaleSwitcher from '@/components/LocaleSwitcher.vue';
import AsideUserCard from '@/components/AsideUserCard.vue';
import { useReloadView } from '@/composables/reloadView';

const { t } = useI18n();
const route = useRoute();
const reloadView = useReloadView();

/**
 * Clicking the already-active menu item fires no navigation (vue-router skips a
 * push to the current route), so the view would never refresh on a re-click.
 * Detect that case and re-mount the view so its content reloads.
 */
function onLeafClick(item: MenuItem): void {
  if (isActiveLeaf(item)) reloadView();
}

interface MenuItem {
  key: string;
  icon: string;
  to?: string;
  i18n: string;
  children?: MenuItem[];
  /** Branch that stays open whenever its children exist (project submodules). */
  autoExpand?: boolean;
}

const BASE_MENU: MenuItem[] = [
  { key: 'dashboard', icon: 'pi pi-th-large', to: '/dashboard', i18n: 'dashboard' },
  { key: 'projects', icon: 'pi pi-folder', to: '/projects', i18n: 'projects' },
  { key: 'reports', icon: 'pi pi-chart-bar', to: '/reports', i18n: 'reports' },
  { key: 'infoCenter', icon: 'pi pi-info-circle', to: '/info-center', i18n: 'infoCenter' },
  { key: 'plans', icon: 'pi pi-calendar', to: '/plans', i18n: 'plans' },
  { key: 'iso', icon: 'pi pi-verified', to: '/iso', i18n: 'iso' },
  { key: 'companies', icon: 'pi pi-building', to: '/companies', i18n: 'companies' },
  { key: 'packages', icon: 'pi pi-box', to: '/packages', i18n: 'packages' },
  {
    key: 'usersManagement',
    icon: 'pi pi-users',
    i18n: 'usersManagement',
    children: [
      { key: 'users', icon: 'pi pi-circle', to: '/users', i18n: 'users' },
      { key: 'roles', icon: 'pi pi-circle', to: '/users/roles', i18n: 'roles' },
    ],
  },
  {
    key: 'systemSettings',
    icon: 'pi pi-cog',
    i18n: 'systemSettings',
    children: [
      { key: 'crafts', icon: 'pi pi-circle', to: '/system-settings/crafts', i18n: 'crafts' },
      { key: 'standards', icon: 'pi pi-circle', to: '/system-settings/standards', i18n: 'standards' },
      { key: 'professions', icon: 'pi pi-circle', to: '/system-settings/professions', i18n: 'professions' },
      { key: 'positions', icon: 'pi pi-circle', to: '/system-settings/positions', i18n: 'positions' },
      { key: 'stages', icon: 'pi pi-circle', to: '/system-settings/stages', i18n: 'stages' },
      { key: 'inspectionsSystem', icon: 'pi pi-circle', to: '/system-settings/inspections', i18n: 'inspectionsSystem' },
    ],
  },
  { key: 'systemPreferences', icon: 'pi pi-sliders-h', to: '/system-preferences', i18n: 'systemPreferences' },
];

/** The active project's id, when the current route is inside a project workspace. */
const activeProjectId = computed<string | null>(() => {
  if (!route.matched.some((r) => r.name === 'project-workspace')) return null;
  const id = route.params.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
});

/**
 * The menu, with the project submodules injected as children under "Projects"
 * whenever a project is open. They live in the Aside (not a content rail), so
 * selecting a project opens its submodules right under the Projects nav item.
 */
const MENU = computed<MenuItem[]>(() =>
  BASE_MENU.map((item) => {
    if (item.key !== 'projects' || !activeProjectId.value) return item;
    return {
      ...item,
      autoExpand: true,
      children: PROJECT_SUBMODULES.map((m) => ({
        key: `project-${m.slug}`,
        icon: 'pi pi-circle',
        to: `/projects/${activeProjectId.value}/${m.slug}`,
        i18n: m.i18n,
      })),
    };
  }),
);

/**
 * Pick the single leaf whose `to` is the LONGEST matching prefix of the
 * current route. Without "longest-wins" `/users` would also light up when
 * the user is on `/users/roles`, because the shorter path is a prefix of
 * the longer one.
 */
const activeLeafKey = computed<string | null>(() => {
  const path = route.path;
  let bestKey: string | null = null;
  let bestLen = -1;
  for (const item of MENU.value) {
    const candidates: MenuItem[] = [item, ...(item.children ?? [])];
    for (const c of candidates) {
      if (!c.to) continue;
      const matches = path === c.to || path.startsWith(`${c.to}/`);
      if (matches && c.to.length > bestLen) {
        bestLen = c.to.length;
        bestKey = c.key;
      }
    }
  }
  return bestKey;
});

function isActiveLeaf(item: MenuItem): boolean {
  return activeLeafKey.value === item.key;
}

/** A parent is "in-path" if any of its children is the active leaf. */
function isInPath(item: MenuItem): boolean {
  return item.children?.some((c) => activeLeafKey.value === c.key) ?? false;
}

/** Per-parent expanded state. Defaults: collapsed unless a child is active. */
const expanded = ref<Record<string, boolean>>({});

function initExpansion(): void {
  for (const item of MENU.value) {
    if (item.children && isInPath(item)) {
      expanded.value[item.key] = true;
    }
  }
}
initExpansion();
watch(() => route.path, initExpansion);

function toggle(key: string): void {
  expanded.value[key] = !expanded.value[key];
}

function isExpanded(key: string): boolean {
  return Boolean(expanded.value[key]);
}
</script>

<template>
  <aside class="qb-aside" :aria-label="t('navigation') || 'Navigation'">
    <div class="qb-aside__brand" aria-hidden="true">
      <img
        class="qb-aside__logo"
        src="/logo.png"
        alt="QB"
        width="286"
        height="174"
      />
    </div>

    <AsideUserCard class="qb-aside__user-card" />

    <nav class="qb-aside__nav">
      <ul class="qb-aside__list">
        <li v-for="item in MENU" :key="item.key" class="qb-aside__item">
          <!-- Pure branch (children, no route of its own): toggles expansion. -->
          <button
            v-if="item.children && !item.to"
            type="button"
            class="qb-aside__link qb-aside__link--branch"
            :class="{ 'qb-aside__link--in-path': isInPath(item) }"
            :aria-expanded="isExpanded(item.key)"
            @click="toggle(item.key)"
          >
            <i class="qb-aside__icon" :class="item.icon" aria-hidden="true" />
            <span class="qb-aside__label">{{ t(item.i18n) }}</span>
            <i
              class="qb-aside__chevron pi"
              :class="isExpanded(item.key) ? 'pi-chevron-down' : 'pi-chevron-right'"
              aria-hidden="true"
            />
          </button>

          <!-- Leaf navigation item (may also carry contextual children, e.g. Projects). -->
          <router-link
            v-else-if="item.to"
            :to="item.to"
            class="qb-aside__link"
            :class="{
              'qb-aside__link--active': isActiveLeaf(item),
              'qb-aside__link--in-path': isInPath(item),
            }"
            @click="onLeafClick(item)"
          >
            <i class="qb-aside__icon" :class="item.icon" aria-hidden="true" />
            <span class="qb-aside__label">{{ t(item.i18n) }}</span>
          </router-link>

          <!-- Children (rendered when expanded, or auto-expanded contextual children). -->
          <ul
            v-if="item.children && (isExpanded(item.key) || item.autoExpand)"
            class="qb-aside__sublist"
          >
            <li v-for="child in item.children" :key="child.key" class="qb-aside__subitem">
              <router-link
                v-if="child.to"
                :to="child.to"
                class="qb-aside__link qb-aside__link--child"
                :class="{ 'qb-aside__link--active': isActiveLeaf(child) }"
                @click="onLeafClick(child)"
              >
                <i class="qb-aside__icon" :class="child.icon" aria-hidden="true" />
                <span class="qb-aside__label">{{ t(child.i18n) }}</span>
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </nav>

    <div class="qb-aside__footer">
      <LocaleSwitcher />
    </div>
  </aside>
</template>

<style scoped>
.qb-aside {
  background: var(--qb-aside-bg);
  color: var(--qb-aside-fg);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px 16px;
  height: 100vh;
  z-index: var(--qb-z-aside);
}

.qb-aside__brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0 16px;
}

.qb-aside__logo {
  /* Real logo is 286×174 (~1.64:1). Constrain by height; width follows the
     natural aspect ratio. */
  width: auto;
  height: 96px;
  max-width: 100%;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

/* User card styles live in AsideUserCard.vue (composite owns its layout). */
.qb-aside__user-card {
  margin-bottom: 8px;
}

.qb-aside__nav {
  flex: 1 1 auto;
  overflow-y: auto;
}

.qb-aside__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qb-aside__link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--qb-radius-md);
  color: var(--qb-aside-fg);
  text-decoration: none;
  font-weight: var(--qb-font-weight-medium);
  font-size: var(--qb-font-size-sm);
  line-height: 1;
  transition: background var(--qb-transition-fast), color var(--qb-transition-fast);
  width: 100%;
  text-align: start;
  background: transparent;
  border: none;
  font-family: inherit;
  cursor: pointer;
}

.qb-aside__link--branch {
  /* Same look as leaf but renders as a button. */
}

.qb-aside__link--in-path {
  /* Parent of an active child — keep neutral, the active child is highlighted. */
  color: var(--qb-aside-fg);
}

.qb-aside__sublist {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qb-aside__subitem {
  padding: 0;
}

.qb-aside__link--child {
  padding-inline-start: 28px;
  font-weight: var(--qb-font-weight-regular);
}

.qb-aside__link--child .qb-aside__icon {
  font-size: 10px;
  opacity: 0.7;
}

.qb-aside__link:hover {
  background: var(--qb-aside-item-hover-bg);
  text-decoration: none;
}

.qb-aside__link--active {
  background: var(--qb-aside-item-active-bg);
  color: var(--qb-aside-item-active-fg);
}

.qb-aside__link--active .qb-aside__icon {
  color: var(--qb-aside-item-active-fg);
}

.qb-aside__icon {
  font-size: 16px;
  inline-size: 18px;
  text-align: center;
  flex: 0 0 auto;
}

.qb-aside__label {
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qb-aside__chevron {
  font-size: 12px;
  opacity: 0.7;
}

[dir='rtl'] .qb-aside__chevron {
  transform: scaleX(-1);
}

.qb-aside__footer {
  display: flex;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
</style>
